import { createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "../../config/api";
import { generateQueryParams } from "../../utils/generateQueryParams";
import { roles } from "../../constants/commonConstants";

// EXAMPLE: Expected Orders Query Parameters
// userId: 64f1a2b3c4d5e6f7g8h9i0j1
// type: USER / TASKER
// offset: 0
// limit: 10
// searchTerm: order123
// status: PENDING
// multipleStatus: PENDING,DELIVERED

// NOTE: Get Client Orders
export const getUserOrders = createAsyncThunk(
  "order/getUserOrders",
  async (params = {}) => {
    const query = generateQueryParams(params);
    const response = await api.get(`orders/user?${query}`);
    return response.data;
  },
);

export const getAllOrders = createAsyncThunk(
  "order/getAllOrders",
  async (params = {}) => {
    const query = generateQueryParams(params);
    const response = await api.get(`/admin/orders?${query}`);
    return response.data;
  },
);

// NOTE: Create Raist Complaint
export const createRaistComplaint = createAsyncThunk(
  "order/raist-complaint",
  async (complaint) => {
    const response = await api.post("order/complaint", complaint);
    return response.data;
  },
);

// NOTE: Get Order Details for Admin
export const getOrderDetailsByAdmin = createAsyncThunk(
  "order/getOrderDetailsByAdmin",
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/admin/orders/${orderId}/details`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || {
          message: "Failed to fetch order details for admin",
        },
      );
    }
  },
);

// NOTE: complete an delivered user order
export const acceptOrder = createAsyncThunk(
  "order/acceptOrder",
  async (orderId) => {
    const response = await api.post("order/complete", orderId);
    return response.data;
  },
);

// NOTE: Create Request Revision
export const createRequestRevision = createAsyncThunk(
  "order/request-revision",
  async (revision, { dispatch }) => {
    const response = await api.post("order/request-revision", revision);
    dispatch(getOrderCounts());
    return response.data;
  },
);

// NOTE: Submit Order Issue on Tasker side
export const createRaiseIssue = createAsyncThunk(
  "order/createRaiseIssue",
  async (data) => {
    const response = await api.post("order/issue", data);
    return response.data;
  },
);

// NOTE: Tasker Side Deliver Job
export const createDeliverJob = createAsyncThunk(
  "order/deliver",
  async (data) => {
    const response = await api.post("order/deliver", data);
    return response.data;
  },
);

// NOTE: add Order Review
export const addOrderReview = createAsyncThunk(
  "order/addReview",
  async (orderId) => {
    const response = await api.post("order/review", orderId);
    return response.data;
  },
);

// NOTE: Get Payment History details
export const fetchPaymentHistoryTasker = createAsyncThunk(
  "/history/getTaskerPaymentHistory",
  async (arg) => {
    const query = generateQueryParams(arg, { type: roles.TASKER });
    const response = await api.get(`payment/history/tasker?${query}`);
    return response.data;
  },
);

export const createPaymentCheckout = createAsyncThunk(
  "order/createPaymentCheckout",
  async ({ eventId }) => {
    const response = await api.post("order/create-payment-intent", { eventId });
    return response.data;
  },
);

export const createSubscriptionCheckout = createAsyncThunk(
  "order/createSubscriptionCheckout",
  async ({ eventId }) => {
    const response = await api.post(
      "order/create-payment-intent-subscription",
      { eventId },
    );
    return response.data;
  },
);

// NOTE: Get available admin team members for an order
export const getAvailableAdminTeamMembers = createAsyncThunk(
  "order/getAvailableAdminTeamMembers",
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `/admin/orders/${orderId}/admin-team-members`,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || {
          message: "Failed to fetch available admin team members",
        },
      );
    }
  },
);

// NOTE: Add admin team members to order
export const addAdminTeamMembers = createAsyncThunk(
  "order/addAdminTeamMembers",
  async (
    {
      orderId,
      adminMembers,
      permissionLevel = "ADMIN_BASIC",
      canManageTeam = false,
      customPermissions = [],
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.post(
        `/admin/orders/${orderId}/admin-team-members`,
        {
          adminMembers,
          permissionLevel,
          canManageTeam,
          customPermissions,
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to add admin team members" },
      );
    }
  },
);

// NOTE: Upload files by admin
export const uploadOrderFilesByAdmin = createAsyncThunk(
  "order/uploadOrderFilesByAdmin",
  async ({ orderId, files, note, category }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/admin/orders/${orderId}/files`, {
        files,
        note,
        category,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to upload files" },
      );
    }
  },
);

// NOTE: Delete file by admin
export const deleteOrderFileByAdmin = createAsyncThunk(
  "order/deleteOrderFileByAdmin",
  async ({ orderId, fileId }, { rejectWithValue }) => {
    try {
      const response = await api.delete(
        `/admin/orders/${orderId}/files/${fileId}`,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to delete file" },
      );
    }
  },
);

export const addOrderNote = createAsyncThunk(
  "order/addOrderNote",
  async ({ orderId, content, attachments = [] }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/admin/orders/${orderId}/notes`, {
        content,
        attachments,
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to add order note" },
      );
    }
  },
);

// Resubmit milestone with changes (admin)
export const resubmitMilestone = createAsyncThunk(
  "order/resubmitMilestone",
  async (
    {
      orderId,
      milestoneId,
      title,
      description,
      amount,
      dueDate,
      notes,
      files = [],
      splitOptionsEnabled = false,
      splitOptions = [],
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const response = await api.put(
        `/admin/orders/${orderId}/milestone/${milestoneId}/resubmit`,
        {
          title,
          description,
          amount,
          dueDate,
          notes,
          files,
          splitOptionsEnabled,
          splitOptions,
        },
      );

      dispatch(getOrderCounts());
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to resubmit milestone" },
      );
    }
  },
);

// Approve milestone (admin)
export const approveMilestone = createAsyncThunk(
  "order/approveMilestone",
  async (
    {
      orderId,
      milestoneId,
      title,
      description,
      amount,
      dueDate,
      notes,
      files = [],
      splitOptionsEnabled = false,
      splitOptions = [],
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const response = await api.put(
        `/admin/orders/${orderId}/milestone/${milestoneId}/approve`,
        {
          title,
          description,
          amount,
          dueDate,
          notes,
          files,
          splitOptionsEnabled,
          splitOptions,
        },
      );

      dispatch(getOrderCounts());
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to approve milestone" },
      );
    }
  },
);

export const addMilestoneNoteByAdmin = createAsyncThunk(
  "adminOrder/addMilestoneNote",
  async (
    {
      orderId,
      milestoneProgressId,
      content,
      parentCommentId,
      attachments = [],
    },
    { rejectWithValue },
  ) => {
    console.log("addMilestoneNoteByAdmin thunk called with:", {
      orderId,
      milestoneProgressId,
      content,
      parentCommentId,
      attachments,
    });

    try {
      const response = await api.post(
        `/admin/orders/${orderId}/milestones/${milestoneProgressId}/comments`,
        {
          content,
          parentCommentId,
          attachments,
        },
      );

      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to add admin comment",
      );
    }
  },
);

// NOTE: Delete order
export const deleteOrder = createAsyncThunk(
  "order/deleteOrder",
  async (orderId) => {
    const response = await api.delete(`/admin/orders/${orderId}`);
    return response.data;
  },
);

// Update order priority
export const updateOrderPriority = createAsyncThunk(
  "order/updateOrderPriority",
  async ({ orderId, priority }) => {
    const response = await api.patch(`/admin/orders/${orderId}/priority`, {
      priority,
    });
    return response.data.data;
  },
);

// Update revision status
export const updateRevisionStatus = createAsyncThunk(
  "order/updateRevisionStatus",
  async ({ orderId, revisionId, status }) => {
    const response = await api.patch(
      `/orders/${orderId}/revisions/${revisionId}/status`,
      { status },
    );
    return response.data.data;
  },
);

// Start Milestone (Admin)
export const startMilestone = createAsyncThunk(
  "adminOrder/startMilestone",
  async ({ orderId, milestoneId }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post(
        `/admin/orders/${orderId}/milestones/${milestoneId}/start`,
      );

      dispatch(getOrderCounts());
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to start milestone" },
      );
    }
  },
);

// Get order counts for all tabs
export const getOrderCounts = createAsyncThunk(
  "order/getOrderCounts",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/admin/orders/counts");
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

// Submit Milestone (Admin)
export const submitMilestone = createAsyncThunk(
  "adminOrder/submitMilestone",
  async (
    {
      orderId,
      milestoneId,
      deliveryNotes,
      files = [],
      note,
      isFinalDeliverable = false,
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const response = await api.post(
        `/admin/orders/${orderId}/milestones/${milestoneId}/submit`,
        {
          deliveryNotes,
          files,
          note,
          isFinalDeliverable,
        },
      );

      dispatch(getOrderCounts());
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to submit milestone" },
      );
    }
  },
);

// Start Revision (Admin)
export const startRevision = createAsyncThunk(
  "adminOrder/startRevision",
  async ({ orderId, milestoneId }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post(
        `/admin/orders/${orderId}/milestones/${milestoneId}/start-revision`,
      );

      dispatch(getOrderCounts());
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to start revision" },
      );
    }
  },
);

// Submit Revision (Admin)
export const submitRevision = createAsyncThunk(
  "adminOrder/submitRevision",
  async (
    {
      orderId,
      milestoneId,
      deliveryNotes,
      files = [],
      note,
      isFinalDeliverable = false,
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const response = await api.post(
        `/admin/orders/${orderId}/milestones/${milestoneId}/submit-revision`,
        {
          deliveryNotes,
          files,
          note,
          isFinalDeliverable,
        },
      );

      dispatch(getOrderCounts());
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to submit revision" },
      );
    }
  },
);

export const createOrder = createAsyncThunk(
  "order/createOrder",
  async (orderData) => {
    const response = await api.post("/user/", orderData);
    return response.data;
  },
);

// Add admin comment to order
export const addAdminComment = createAsyncThunk(
  "order/addAdminComment",
  async (
    { orderId, content, parentCommentId = null, attachments = [] },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.post(
        `/admin/orders/${orderId}/admin-comments`,
        {
          content,
          parentCommentId,
          attachments,
        },
      );

      return response.data;
    } catch (error) {
      console.error("Thunk: Error:", error.response?.data || error);
      return rejectWithValue(
        error.response?.data || { message: "Failed to add admin comment" },
      );
    }
  },
);

// Delete Order Feedback (Admin only)
export const deleteOrderFeedback = createAsyncThunk(
  "order/deleteOrderFeedback",
  async ({ orderId, feedbackId }, { rejectWithValue }) => {
    try {
      const response = await api.delete(
        `/admin/orders/${orderId}/feedback/${feedbackId}`,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Delete Milestone Feedback (Admin only)
export const deleteMilestoneFeedback = createAsyncThunk(
  "order/deleteMilestoneFeedback",
  async ({ orderId, milestoneId, feedbackId }, { rejectWithValue }) => {
    try {
      const response = await api.delete(
        `/admin/orders/${orderId}/milestones/${milestoneId}/feedback/${feedbackId}`,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const updateDisputeStatus = createAsyncThunk(
  "order/updateDisputeStatus",
  async ({ orderId, disputeId, status, adminNote }, { rejectWithValue }) => {
    try {
      const response = await api.patch(
        `/admin/orders/${orderId}/disputes/${disputeId}/status`,
        {
          status,
          adminNote,
        },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const addDisputeAdminNote = createAsyncThunk(
  "order/addDisputeAdminNote",
  async ({ orderId, disputeId, content, files = [] }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/admin/orders/${orderId}/disputes/${disputeId}/notes`,
        {
          content,
          files,
        },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const startOrderDevelopment = createAsyncThunk(
  "order/startOrderDevelopment",
  async ({ orderId }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.put(`/admin/orders/${orderId}/start-order`);
      dispatch(getOrderCounts());
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || {
          message: error.message || "Failed to start order development",
        },
      );
    }
  },
);

export const submitOrder = createAsyncThunk(
  "order/submitOrder",
  async (
    { orderId, files, note, isFinalDeliverable },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const response = await api.put(`/admin/orders/${orderId}/submit`, {
        files,
        note,
        isFinalDeliverable,
      });
      dispatch(getOrderCounts());
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || {
          message: error.message || "Failed to submit order",
        },
      );
    }
  },
);

// Start Order-level Revision (Admin)
export const startOrderRevision = createAsyncThunk(
  "adminOrder/startOrderRevision",
  async ({ orderId }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post(
        `/admin/orders/${orderId}/start-revision`,
      );

      dispatch(getOrderCounts());
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to start order revision" },
      );
    }
  },
);

// Submit Order-level Revision (Admin)
export const submitOrderRevision = createAsyncThunk(
  "adminOrder/submitOrderRevision",
  async (
    { orderId, deliveryNotes, files = [], note, isFinalDeliverable = false },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const response = await api.post(
        `/admin/orders/${orderId}/submit-revision`,
        {
          deliveryNotes,
          files,
          note,
          isFinalDeliverable,
        },
      );

      dispatch(getOrderCounts());
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to submit order revision" },
      );
    }
  },
);
